import { useRef, useCallback, useEffect } from 'react';
import { Minus, Maximize2, Minimize2, X, ArrowDownToLine } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PanelState, PanelPosition } from '@/types/communication-panels';

interface FloatingPanelProps {
  title: string;
  icon: React.ReactNode;
  state: PanelState;
  position: PanelPosition;
  zIndex: number;
  onClose: () => void;
  onDock: () => void;
  onPositionChange: (pos: Partial<PanelPosition>) => void;
  onFocus: () => void;
  children: React.ReactNode;
}

const MIN_WIDTH = 300;
const MIN_HEIGHT = 200;
const HEADER_HEIGHT = 40;

export function FloatingPanel({
  title,
  icon,
  state,
  position,
  zIndex,
  onClose,
  onDock,
  onPositionChange,
  onFocus,
  children,
}: FloatingPanelProps) {
  const dragRef = useRef({ dragging: false, offsetX: 0, offsetY: 0 });
  const resizeRef = useRef({ resizing: false, startX: 0, startY: 0, startW: 0, startH: 0 });

  const onDragPointerDown = useCallback((e: React.PointerEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button')) return;
    e.preventDefault();
    dragRef.current = { dragging: true, offsetX: e.clientX - position.x, offsetY: e.clientY - position.y };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    onFocus();
  }, [position.x, position.y, onFocus]);

  const onDragPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current.dragging) return;
    const x = Math.max(0, Math.min(window.innerWidth - position.width, e.clientX - dragRef.current.offsetX));
    const y = Math.max(0, Math.min(window.innerHeight - HEADER_HEIGHT, e.clientY - dragRef.current.offsetY));
    onPositionChange({ x, y });
  }, [position.width, onPositionChange]);

  const onDragPointerUp = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current.dragging) return;
    dragRef.current.dragging = false;
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
  }, []);

  const onResizePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = { resizing: true, startX: e.clientX, startY: e.clientY, startW: position.width, startH: position.height };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    onFocus();
  }, [position.width, position.height, onFocus]);

  const onResizePointerMove = useCallback((e: React.PointerEvent) => {
    if (!resizeRef.current.resizing) return;
    const dw = e.clientX - resizeRef.current.startX;
    const dh = e.clientY - resizeRef.current.startY;
    const width = Math.max(MIN_WIDTH, Math.min(window.innerWidth - position.x, resizeRef.current.startW + dw));
    const height = Math.max(MIN_HEIGHT, Math.min(window.innerHeight - position.y, resizeRef.current.startH + dh));
    onPositionChange({ width, height });
  }, [position.x, position.y, onPositionChange]);

  const onResizePointerUp = useCallback((e: React.PointerEvent) => {
    if (!resizeRef.current.resizing) return;
    resizeRef.current.resizing = false;
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
  }, []);

  // ESC for at lukke panel
  useEffect(() => {
    if (state === 'closed') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state, onClose]);

  if (state === 'closed') return null;

  return (
    <div
      onPointerDown={onFocus}
      className="fixed bg-background border border-border rounded-xl shadow-2xl flex flex-col overflow-hidden"
      style={{
        left: position.x,
        top: position.y,
        width: position.width,
        height: position.height,
        zIndex,
      }}
    >
      {/* Drag header */}
      <div
        onPointerDown={onDragPointerDown}
        onPointerMove={onDragPointerMove}
        onPointerUp={onDragPointerUp}
        className="flex items-center justify-between px-3 border-b border-border bg-muted/50 select-none shrink-0 h-10 cursor-move transition-colors duration-150"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="shrink-0">{icon}</span>
          <span className="text-sm font-medium text-foreground truncate">{title}</span>
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          <button
            onClick={(e) => { e.stopPropagation(); onDock(); }}
            className="w-7 h-7 rounded hover:bg-secondary flex items-center justify-center"
            title="Dock i panel"
          >
            <ArrowDownToLine className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="w-7 h-7 rounded hover:bg-secondary flex items-center justify-center"
            title="Luk"
          >
            <X className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        </div>
      </div>

      {/* Content — overflow-y-auto sikrer scrolling inde i panelet */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden relative">
        {children}
        {/* Resize handle */}
        <div
          onPointerDown={onResizePointerDown}
          onPointerMove={onResizePointerMove}
          onPointerUp={onResizePointerUp}
          className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize z-10"
          style={{ touchAction: 'none' }}
        >
          <svg viewBox="0 0 16 16" className="w-full h-full text-muted-foreground/40">
            <path d="M14 14L8 14M14 14L14 8M14 14L4 14M14 14L14 4" stroke="currentColor" strokeWidth="1.5" fill="none" />
          </svg>
        </div>
      </div>
    </div>
  );
}
